import { useEffect, useRef, useState } from "react";

function sameDeps(a, b) {
  return a.length === b.length && a.every((v, i) => Object.is(v, b[i]));
}

/**
 * Run an async fetch when `deps` change, tracking loading and error state.
 *
 * The page-level pattern this replaces was written out twice and is easy to
 * get subtly wrong in two ways.
 *
 * Without the cancelled flag, a slow request for an old date range resolves
 * after a fast one for the new range and overwrites it, so the screen shows
 * numbers for dates the picker is no longer displaying.
 *
 * And the render that first sees new deps happens *before* the effect that
 * fetches for them, so a naive version hands back the previous deps' data with
 * `loading` still false — one frame of the last vendor's orders under the new
 * vendor's name. Comparing deps during render closes that window: data is only
 * ever returned for the deps it was fetched with, and anything else reads as
 * still loading.
 *
 * @param {() => Promise<any>} fetcher - must be stable, or wrapped in useCallback
 * @param {any[]} deps - re-fetch when these change (compared by identity)
 * @param {{ enabled?: boolean, errorMessage?: string }} [options]
 *        `enabled: false` holds off entirely — for a page that has nothing to
 *        fetch until something is selected.
 * @returns {{ data: any, loading: boolean, error: string }}
 */
export default function useAsyncData(fetcher, deps, options = {}) {
  const { enabled = true, errorMessage = "Failed to load data." } = options;

  const [state, setState] = useState({ data: null, error: "" });
  const settledFor = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    fetcher()
      .then((data) => {
        if (cancelled) return;
        settledFor.current = deps;
        setState({ data, error: "" });
      })
      .catch((err) => {
        if (cancelled) return;
        settledFor.current = deps;
        setState({ data: null, error: err.message || errorMessage });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  const settled =
    settledFor.current !== null && sameDeps(settledFor.current, deps);

  return {
    data: settled ? state.data : null,
    loading: enabled && !settled,
    error: settled ? state.error : "",
  };
}
